import { Injectable } from '@angular/core';
import { MobilePaymentType } from '@sales/enums/mobile-payment-type';
import { MobilePhoneProductPrice } from '@sales/models/mobilephone-product-price';
import { VoiceProduct } from '@shared/models/voice-product.model';
import { MobilePhoneProduct } from '@sales/models/mobilephone-product';
import { PaymentType } from '@shared/enums/payment-type';
import { Price } from '@shared/models/price.model';
import { mapAmountPriceToPrice } from '@app/core/price-utils';

@Injectable({
  providedIn: 'root',
})
export class MobilePhoneProductHelperService {
  static getMobilePhonePrice(
    mobilePhoneProduct: MobilePhoneProduct,
    voiceProducts: VoiceProduct[]
  ): MobilePhoneProductPrice {
    if (mobilePhoneProduct.mobilePhoneVariant && mobilePhoneProduct.paymentType) {
      if (
        mobilePhoneProduct.paymentType === MobilePaymentType.EquipmentUpgrade &&
        mobilePhoneProduct.mobilePhoneVariant.hasMobilBytte
      ) {
        return this.getMobilbyttePrice(mobilePhoneProduct);
      } else if (
        mobilePhoneProduct.paymentType === MobilePaymentType.PayNowWithSubscription &&
        mobilePhoneProduct.mobilePhoneVariant.paymentInformation?.subscriptionBundleOptions?.length > 0 &&
        mobilePhoneProduct.voiceProductId &&
        mobilePhoneProduct.sendInPost
      ) {
        return this.getSubscriptionBundlePrice(mobilePhoneProduct, voiceProducts);
      } else if (
        mobilePhoneProduct.paymentType === MobilePaymentType.PayNowWithoutSubscription &&
        mobilePhoneProduct.sendInPost
      ) {
        const price = new MobilePhoneProductPrice();
        price.totalPrice = mobilePhoneProduct.mobilePhoneVariant.priceInformation?.price;
        price.agreementLength = 0;
        return price;
      }
    }
    return { monthlyPrice: new Price(), totalPrice: new Price(), agreementLength: 0 };
  }

  static getBundleIdForSelectedMobileAndVoiceProduct(
    mobilePhoneProduct: MobilePhoneProduct,
    voiceProducts: VoiceProduct[]
  ): string {
    if (mobilePhoneProduct.mobilePhoneVariant && mobilePhoneProduct.voiceProductId && mobilePhoneProduct.paymentType) {
      const connectedVoiceOfferingId = voiceProducts.find(v => v.id === mobilePhoneProduct.voiceProductId)?.offering?.id;
      if (!connectedVoiceOfferingId) return null;

      switch (mobilePhoneProduct.paymentType) {
        case MobilePaymentType.PayNowWithSubscription:
          const selectedSubscriptionBundle =
            mobilePhoneProduct.mobilePhoneVariant.paymentInformation.subscriptionBundleOptions.find(
              o => o.subscriptionOfferingId === connectedVoiceOfferingId
            );
          return selectedSubscriptionBundle?.offeringId;
        default:
          break;
      }
    }
    return null;
  }

  static getMobilbyttePrice({ mobilePhoneVariant, mobilBytteOffering }: MobilePhoneProduct) {
    const price = new MobilePhoneProductPrice();
    if (mobilBytteOffering) {
      const selectedMobilBytteOffering = mobilePhoneVariant.mobilbytteOfferings.mobilbytteOfferings.find(
        mbo => mbo.offeringId === mobilBytteOffering
      );
      price.monthlyPrice = mapAmountPriceToPrice(selectedMobilBytteOffering.monthlyPrice);
      price.totalPrice = mapAmountPriceToPrice(selectedMobilBytteOffering.totalPriceAfterAgreementPeriod);
      price.agreementLength = selectedMobilBytteOffering.agreementPeriodInMonths;
    }

    return price;
  }

  static getSubscriptionBundlePrice(
    { mobilePhoneVariant, voiceProductId }: MobilePhoneProduct,
    voiceProducts: VoiceProduct[]
  ) {
    const connectedVoiceProduct = voiceProducts.find(x => x.id === voiceProductId);
    const price = new MobilePhoneProductPrice();
    if (
      connectedVoiceProduct?.isExistingSubscription &&
      (!connectedVoiceProduct.offering || !connectedVoiceProduct.offering.id)
    ) {
      price.totalPrice = mobilePhoneVariant.priceInformation.price;
      price.agreementLength = 0;
      return price;
    }

    const selectedBundleOption = mobilePhoneVariant.paymentInformation.subscriptionBundleOptions.find(
      o => o.subscriptionOfferingId === connectedVoiceProduct?.offering?.id
    );
    price.totalPrice = selectedBundleOption?.priceInformation?.price;
    price.agreementLength = 0;
    return price;
  }

  static getMobilePaymentType(mobilePhoneProduct: MobilePhoneProduct): PaymentType {
    switch (mobilePhoneProduct.paymentType) {
      case MobilePaymentType.EquipmentUpgrade:
        return PaymentType.EquipmentUpgrade;
      default:
        if (mobilePhoneProduct.sendInPost) {
          return PaymentType.Upfront;
        }
        return PaymentType.Billing;
    }
  }

  static getMobilePaymentName(mobilePhoneProduct: MobilePhoneProduct): string {
    switch (mobilePhoneProduct.paymentType) {
      case MobilePaymentType.EquipmentUpgrade:
        return 'MobilBytte';
      case MobilePaymentType.PayNowWithSubscription:
      case MobilePaymentType.PayNowWithoutSubscription:
        return 'Betal nå';
      default:
        if (mobilePhoneProduct.sendInPost) {
          return 'Betal nå';
        }
        return 'Månedelig betaling';
    }
  }
}
